/**
 * Normaliza fotos do celular antes do envio.
 *
 * O seletor de arquivos usa `accept="image/*"`, então no iPhone a opção "Câmera" entrega
 * HEIC/HEIF — formato que o servidor (Sharp/libvips) não decodifica. Aqui convertemos para
 * JPEG e ainda reduzimos a imagem quando ela é grande demais, para o upload não estourar o
 * limite do servidor nem a internet do celular.
 *
 * Se alguma etapa falhar devolvemos o arquivo original: aí quem reprova é o servidor, com
 * mensagem clara, em vez de a foto sumir silenciosamente da fila.
 */

/** Formatos que o navegador entrega mas o servidor não sabe ler. */
const NEEDS_DECODING = /^image\/(heic|heif|heif-sequence|avif)$/i;

const MAX_SIDE = 2000;
/** Abaixo disso não vale a pena re-comprimir. */
const MAX_BYTES = 2_500_000;
const HEIC_CDN = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';

type Heic2Any = (opts: { blob: Blob; toType: string; quality?: number }) => Promise<Blob | Blob[]>;

let heicLoader: Promise<Heic2Any | null> | null = null;

/** Carrega o heic2any por <script> (é UMD) e expõe a função em window.heic2any. */
function loadHeic2Any(): Promise<Heic2Any | null> {
  if (heicLoader) return heicLoader;
  heicLoader = new Promise((resolve) => {
    const existing = (window as { heic2any?: Heic2Any }).heic2any;
    if (existing) return resolve(existing);
    const script = document.createElement('script');
    script.src = HEIC_CDN;
    script.async = true;
    script.onload = () => resolve((window as { heic2any?: Heic2Any }).heic2any ?? null);
    script.onerror = () => resolve(null); // sem internet ou bloqueado: cai no arquivo original
    document.head.appendChild(script);
  });
  return heicLoader;
}

async function blobToFile(blob: Blob, original: File): Promise<File> {
  const name = original.name.replace(/\.[^.]+$/, '') || 'foto';
  return new File([blob], `${name}.jpg`, { type: 'image/jpeg', lastModified: original.lastModified });
}

/** Converte HEIC/HEIF/AVIF em JPEG. Devolve null se não der (formato desconhecido ou script bloqueado). */
async function convertUndecodable(file: File): Promise<File | null> {
  const heic2any = await loadHeic2Any();
  if (!heic2any) return null;
  try {
    const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(out) ? out[0] : out;
    return blob ? await blobToFile(blob, file) : null;
  } catch {
    return null;
  }
}

/** Reduz a imagem para no máx. MAX_SIDE px e recomprime em JPEG. */
async function shrink(file: File): Promise<File | null> {
  let bitmap: ImageBitmap;
  try {
    // from-image aplica a rotação do EXIF antes de desenhar — sem ela a foto sairia deitada.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return null;
  }
  try {
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    return blob ? await blobToFile(blob, file) : null;
  } catch {
    return null;
  } finally {
    bitmap.close();
  }
}

/**
 * Deixa a foto em JPEG, no tamanho e na resolução que o servidor aceita.
 * Nunca lança: se não conseguir converter, devolve o arquivo original.
 */
export async function normalizeImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  try {
    if (NEEDS_DECODING.test(file.type)) {
      const converted = await convertUndecodable(file);
      if (!converted) return file;
      const smaller = await shrink(converted);
      return smaller ?? converted;
    }
    if (file.size > MAX_BYTES) {
      return (await shrink(file)) ?? file;
    }
    return file;
  } catch {
    return file;
  }
}

/** Aplica {@link normalizeImage} a vários arquivos em paralelo (ordem preservada). */
export const normalizeImages = (files: File[]) => Promise.all(files.map(normalizeImage));
